// Long-press detector for touch devices. Mirrors the right-click affordance
// on mobile: hold for `delay` ms (default 500) without moving more than
// `moveTolerancePx` pixels and the callback fires with the touch coordinates.
//
// Movement, touchend, touchcancel, and a follow-up scroll all cancel the
// pending fire. We deliberately don't trigger when the user starts a tap and
// quickly lifts — that's a normal click.
//
// Returns props you spread on the target element.

import { useCallback, useRef } from 'react';

export interface LongPressHandlers {
  onTouchStart: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchMove: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchEnd: (e: React.TouchEvent<HTMLElement>) => void;
  onTouchCancel: (e: React.TouchEvent<HTMLElement>) => void;
}

export interface LongPressOptions {
  delayMs?: number;
  moveTolerancePx?: number;
}

export function useLongPress(
  callback: (point: { x: number; y: number }) => void,
  { delayMs = 500, moveTolerancePx = 10 }: LongPressOptions = {},
): LongPressHandlers {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);

  const cancel = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    start.current = null;
  }, []);

  const onTouchStart = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      const touch = e.touches[0];
      if (!touch) return;
      start.current = { x: touch.clientX, y: touch.clientY };
      timer.current = setTimeout(() => {
        if (start.current) callback({ x: start.current.x, y: start.current.y });
        timer.current = null;
      }, delayMs);
    },
    [callback, delayMs],
  );

  const onTouchMove = useCallback(
    (e: React.TouchEvent<HTMLElement>) => {
      if (!start.current) return;
      const touch = e.touches[0];
      if (!touch) return;
      const dx = touch.clientX - start.current.x;
      const dy = touch.clientY - start.current.y;
      if (Math.hypot(dx, dy) > moveTolerancePx) cancel();
    },
    [cancel, moveTolerancePx],
  );

  const onTouchEnd = useCallback(() => cancel(), [cancel]);
  const onTouchCancel = useCallback(() => cancel(), [cancel]);

  return { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel };
}
